import React, { Fragment, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import config from '../../../../config';

import ReportShell from '../shared/ReportShell';
import ReportFilterBar from '../shared/ReportFilterBar';
import ReportTable, { formatCount } from '../shared/ReportTable';
import ReportPagination from '../shared/ReportPagination';
import ReportExportButtons from '../shared/ReportExportButtons';
import ColumnPicker from '../shared/ColumnPicker';
import useColumnConfig from '../shared/useColumnConfig';
import useReportLookups from '../shared/useReportLookups';
import useReportAccess from '../shared/useReportAccess';

const PAGE_SIZE = 50;

const yesterday = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().split('T')[0];
};
const YESTERDAY = yesterday();

const ALL_COLUMNS = [
    { key: 'tenant_store_name', label: 'Tenant Name' },
    { key: 'tenant_group_name', label: 'Group Name' },
    { key: 'unit_code', label: 'Unit Code' },
    { key: 'tenant_code', label: 'Tenant Code' },
    { key: 'property_name', label: 'Property' },
    { key: 'total_missing_dates', label: 'Total Missing days', align: 'right', format: formatCount, isCount: true },
    { key: 'missing_dates', label: 'Missing Dates', sortable: false },
];

const DEFAULT_COLUMNS = ALL_COLUMNS.map((c) => c.key);

const initialFilters = {
    startDate: '',
    endDate: '',
    property_id: '',
    group_name: '',
    tenant_id: '',
};

const MissingDatesView = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const {
        selectedColumns, onChange: onColumnsChange, columnOptions,
        visibleColumns, exportColumns,
    } = useColumnConfig(ALL_COLUMNS, DEFAULT_COLUMNS);
    const [reportData, setReportData] = useState([]);
    const [hasRun, setHasRun] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(0);
    const [sort, setSort] = useState({ key: '', dir: 'asc' });

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        groupName: filters.group_name,
    });
    const { concessionaireId, propertyAccess } = lookups;

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'property_id') next.tenant_id = '';
            if (key === 'group_name') next.tenant_id = '';
            return next;
        });
        setReportData([]);
        setHasRun(false);
        setPage(0);
    };

    const resetFilters = () => {
        setFilters(initialFilters);
        setReportData([]);
        setHasRun(false);
        setPage(0);
        setError('');
        setSort({ key: '', dir: 'asc' });
    };

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const generateReport = async () => {
        setLoading(true);
        setError('');
        setPage(0);
        const payload = {
            concessionaire_id: concessionaireId,
            property_access_id: propertyAccess,
            start_date: filters.startDate,
            end_date: filters.endDate,
            group_name: filters.group_name,
            property_id: filters.property_id,
            tenant_id: filters.tenant_id,
        };
        try {
            const res = await axios.post(`${config.hostname}/reports/getmissingdates`, payload, { headers });
            setReportData(Array.isArray(res.data) ? res.data : []);
            setHasRun(true);
            insertToLog(' generated Missing Dates report');
        } catch (err) {
            console.error('missing dates report failed', err);
            setError(err?.response?.data?.message || t('The report request failed. Please adjust your filters and try again.'));
            toast.error(t('Failed to generate Missing Dates report'));
        } finally {
            setLoading(false);
        }
    };

    const sortedData = useMemo(() => {
        const data = [...reportData];
        if (!sort.key) return data;
        data.sort((a, b) => {
            const av = a[sort.key]; const bv = b[sort.key];
            if (av < bv) return sort.dir === 'asc' ? -1 : 1;
            if (av > bv) return sort.dir === 'asc' ? 1 : -1;
            return 0;
        });
        return data;
    }, [reportData, sort]);

    const pageCount = Math.ceil(sortedData.length / PAGE_SIZE);
    const pageRows = sortedData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

    const requestSort = (key) => {
        setSort((prev) => ({ key, dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc' }));
    };

    const fields = [
        { key: 'startDate', label: t('Start date'), type: 'date', role: 'startDate', max: YESTERDAY },
        { key: 'endDate', label: t('End date'), type: 'date', role: 'endDate', max: YESTERDAY },
        { key: 'property_id', label: t('Property'), type: 'select', options: lookups.propertyOptions, placeholder: t('All properties') },
        { key: 'group_name', label: t('Group'), type: 'select', options: lookups.groupOptions, placeholder: t('All groups') },
        { key: 'tenant_id', label: t('Tenant'), type: 'select', options: lookups.tenantOptions, placeholder: t('All tenants') },
    ];

    const exportMetadata = useMemo(() => {
        const dates = filters.startDate && filters.endDate
            ? [[t('From'), filters.startDate], [t('To'), filters.endDate]]
            : [[t('Date range'), t('All available')]];
        return [
            ...dates,
            [t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')],
            [t('Group'), filters.group_name || t('All')],
            [t('Tenant'), nameOf(lookups.tenantOptions, filters.tenant_id) || t('All')],
            [t('Exported By'), login_name],
            [t('Exported On'), new Date().toLocaleString()],
        ];
    }, [filters, lookups, login_name, t]);

    if (!allowed) return null;

    return (
        <Fragment>
            <ReportShell
                title={t('Missing Dates Report')}
                subtitle={t('Tenants with missing transaction dates over the selected period.')}
                actions={
                    <ReportExportButtons
                        heading={t('Missing Dates Report')}
                        metadata={exportMetadata}
                        columns={exportColumns}
                        data={sortedData}
                        fileName="MissingDates"
                        landscape
                        onAfterExport={(fmt) => insertToLog(` exported Missing Dates report to ${fmt}`)}
                    />
                }
            >
                <ReportFilterBar
                    fields={fields}
                    values={filters}
                    onChange={onFilterChange}
                    onGenerate={generateReport}
                    onReset={resetFilters}
                    loading={loading}
                />

                <ColumnPicker
                    options={columnOptions}
                    value={selectedColumns}
                    total={ALL_COLUMNS.length}
                    onChange={onColumnsChange}
                />

                <ReportTable
                    columns={visibleColumns}
                    rows={pageRows}
                    sort={sort}
                    onSort={requestSort}
                    loading={loading}
                    error={error}
                    rowClassName={(item) => (item.total_missing_dates >= 30 ? 'v3-row-alert' : '')}
                    emptyMessage={hasRun
                        ? t('No missing dates found for the selected filters.')
                        : t('Choose your filters and select Generate report to view results.')}
                />

                <ReportPagination
                    page={page}
                    pageCount={pageCount}
                    onChange={setPage}
                    totalItems={sortedData.length}
                    pageSize={PAGE_SIZE}
                    itemLabel={t('tenants')}
                />
            </ReportShell>
        </Fragment>
    );
};

export default MissingDatesView;
